/**
 * Client-side authentication utilities
 * Password verification happens entirely in the browser using SHA-256 hashing.
 */

// SHA-256 hash of the shared password.
// To regenerate: echo -n 'YOUR_PASSWORD' | shasum -a 256 | awk '{print $1}'
const PASSWORD_HASH = '10de3e1316f34e80f2884c29cdaef416c7bc923efea870b0c04a0cde43cbddcd';

const AUTH_STORAGE_KEY = 'hvg-org-chart-auth';
const SESSION_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface AuthSession {
  authenticated: boolean;
  expiresAt: number;
}

async function sha256(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Verify password client-side by comparing SHA-256 hashes.
 * On success, stores a session in localStorage with 7-day expiry.
 */
export async function verifyPassword(password: string): Promise<{ success: boolean; message?: string }> {
  try {
    const hash = await sha256(password);
    if (hash === PASSWORD_HASH) {
      const session: AuthSession = {
        authenticated: true,
        expiresAt: Date.now() + SESSION_DURATION_MS,
      };
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
      return { success: true };
    }
    return { success: false, message: 'Ongeldig wachtwoord' };
  } catch {
    return { success: false, message: 'Er is een fout opgetreden. Probeer het opnieuw.' };
  }
}

/**
 * Check if user is authenticated via localStorage session.
 */
export function checkAuth(): boolean {
  try {
    const data = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!data) return false;
    const session: AuthSession = JSON.parse(data);
    if (!session.authenticated) return false;
    if (Date.now() > session.expiresAt) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Clear session and reload page.
 */
export function logout(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  window.location.reload();
}
